import { useWorkflow } from '@/hooks/use-workflow-context';
import { JournalingStepDefinitionIds, WorkflowIds } from '@/utils/workflows';
import { useCallback } from 'react';

/**
 * Starts the journaling workflow from any entry point (guide completion, the
 * profile "Start Journaling" button, or the home promo dialog).
 *
 * Restarting resolves to the terminal step when there is no restartable
 * in-progress journaling instance — the user's first-ever journaling session or
 * a previously-completed one. In that case we start the workflow fresh so the
 * entry flow opens on the intro step; otherwise the journal-entry screen sees
 * END_WORKFLOW and either redirects the user straight back out or (once the
 * fresh-session redirect guard suppresses that redirect) leaves them on the
 * loading screen with no exit.
 *
 * All journaling entry points must go through this helper so the END_WORKFLOW
 * fallback stays consistent across them.
 */
export function useStartJournalingWorkflow() {
    const { restartWorkflow, startWorkflow } = useWorkflow();

    const startJournalingWorkflow = useCallback(async () => {
        const restartedStep = await restartWorkflow(WorkflowIds.JOURNALING);
        if (restartedStep === JournalingStepDefinitionIds.END_WORKFLOW) {
            await startWorkflow({
                workflowDefnId: WorkflowIds.JOURNALING,
                shouldClearCurrentStep: true,
            });
        }
    }, [restartWorkflow, startWorkflow]);

    return { startJournalingWorkflow };
}
